import { CatmullRomCurve3, CylinderGeometry, TubeGeometry, Vector3, type BufferGeometry } from 'three'
import type { Ponto2 } from '../componentes/contornos2d'
import { aneisDePega, torno } from '../componentes/malhasProcedurais'
import type { NivelQualidade } from '../ambiente/qualidadeGrafica'

const DETALHE: Record<NivelQualidade, { torno: number; tubo: number }> = {
  alta: { torno: 40, tubo: 48 },
  media: { torno: 28, tubo: 32 },
  baixa: { torno: 16, tubo: 16 },
}

/**
 * Corpo laranja de -0,52 a 0, com o anel traseiro de reforço e o colar
 * grafite à frente num perfil só. Cores diferentes vão em malhas diferentes,
 * então o anel e o colar têm perfis próprios.
 */
const PERFIL_CORPO: Ponto2[] = [
  [-0.5, 0],
  [-0.5, 0.05],
  [-0.496, 0.054],
  [-0.05, 0.054],
  [-0.046, 0.05],
  [-0.04, 0.05],
  [-0.036, 0],
]

const PERFIL_ANEL_TRASEIRO: Ponto2[] = [
  [-0.522, 0],
  [-0.522, 0.05],
  [-0.518, 0.058],
  [-0.488, 0.058],
  [-0.484, 0.054],
  [-0.484, 0],
]

const PERFIL_COLAR: Ponto2[] = [
  [-0.04, 0],
  [-0.04, 0.06],
  [-0.034, 0.066],
  [0.026, 0.066],
  [0.03, 0.062],
  [0.03, 0.066],
  [0.034, 0.066],
  [0.046, 0.054],
  [0.046, 0],
]

/** Anel guia e de vedação na boca de cada estágio. */
function perfilAnelGuia(raio: number): Ponto2[] {
  return [
    [-0.011, 0],
    [-0.011, raio - 0.002],
    [-0.008, raio + 0.003],
    [0.008, raio + 0.003],
    [0.011, raio],
    [0.011, 0],
  ]
}

/** Manopla de comando vertical: base com anel seletor, corpo estriado e tampa. */
function perfilManopla(): Ponto2[] {
  return [
    [0, 0],
    [0, 0.04],
    [0.03, 0.036],
    [0.04, 0.028],
    [0.044, 0.024],
    ...aneisDePega(0.046, 0.17, 0.023, 0.0028, 5),
    [0.174, 0.023],
    [0.178, 0.022],
    [0.192, 0.021],
    [0.196, 0.016],
    [0.196, 0],
  ]
}

/** Base da garra em cruz. */
const PERFIL_GARRA: Ponto2[] = [
  [-0.019, 0],
  [-0.019, 0.042],
  [-0.014, 0.046],
  [0.012, 0.046],
  [0.019, 0.038],
  [0.019, 0],
]

/**
 * Tubo de retorno do óleo: sai do colar, corre sobre o corpo e mergulha no
 * anel traseiro. As curvas nas pontas são o que faz ele ler como tubulação
 * presa à ferramenta, e não uma vareta encostada.
 */
function tuboOleo(segmentos: number) {
  const caminho = new CatmullRomCurve3([
    new Vector3(-0.02, 0.06, 0),
    new Vector3(-0.04, 0.066, 0),
    new Vector3(-0.08, 0.064, 0),
    new Vector3(-0.44, 0.064, 0),
    new Vector3(-0.48, 0.062, 0),
    new Vector3(-0.5, 0.054, 0),
  ])
  return new TubeGeometry(caminho, segmentos, 0.0055, 8, false)
}

export interface MalhasExtensor {
  readonly corpo: BufferGeometry
  readonly anelTraseiro: BufferGeometry
  readonly colar: BufferGeometry
  readonly anelGuia1: BufferGeometry
  readonly anelGuia2: BufferGeometry
  readonly manopla: BufferGeometry
  readonly garra: BufferGeometry
  readonly tuboOleo: BufferGeometry
  /** Tubo de comprimento 1 ao longo de x, centrado: o estágio estica por escala. */
  readonly estagio: BufferGeometry
}

const CACHE = new Map<NivelQualidade, MalhasExtensor>()

/** Geometrias do extensor por nível, montadas uma vez e compartilhadas (ver `malhasAlargador`). */
export function malhasExtensor(nivel: NivelQualidade): MalhasExtensor {
  const existente = CACHE.get(nivel)
  if (existente) return existente

  const d = DETALHE[nivel]
  const estagio = new CylinderGeometry(1, 1, 1, d.torno)
  estagio.rotateZ(Math.PI / 2)
  const malhas: MalhasExtensor = {
    corpo: torno(PERFIL_CORPO, d.torno),
    anelTraseiro: torno(PERFIL_ANEL_TRASEIRO, d.torno),
    colar: torno(PERFIL_COLAR, d.torno),
    anelGuia1: torno(perfilAnelGuia(0.039), d.torno),
    anelGuia2: torno(perfilAnelGuia(0.028), d.torno),
    // a manopla é vertical: o torno deita em x, e a cena a põe de pé
    manopla: torno(perfilManopla(), Math.max(16, d.torno - 12)),
    garra: torno(PERFIL_GARRA, d.torno),
    tuboOleo: tuboOleo(d.tubo),
    estagio,
  }
  CACHE.set(nivel, malhas)
  return malhas
}
